import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { commonComponentImages } from '@images/common-components-images';
import { isMenuLinkAllowed } from '@constant/assistant-access-modules';
import { SideBar } from '@models/side-bar.model';
import { ChatStore } from '@modules/chat/services/chat.store';
import { DashService } from '@modules/dash-board/dash.service';
import { ActingAsService } from '@services/acting-as.service';
import { Crypto } from '@services/helpers/encryption-utils';
import { FeatureFlagService } from '@services/helpers/feature-flag.service';
import { ReduxService } from '@state-management/redux.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
})
export class SidebarComponent implements OnInit {
  menu: SideBar[] = [];
  images: any = commonComponentImages;
  isCollapsed: boolean = true;
  @Output() toggleClick = new EventEmitter<boolean>();
  isMobileDevice: boolean = false;
  private resizeSubscription: Subscription;
  activeRoute: string = '';
  constructor(
    private dash: DashService,
    private router: Router,
    private redux: ReduxService,
    private flagService: FeatureFlagService,
    private actingAs: ActingAsService,
    /** Read-only here — the Chat row shows its unread count. The store is filled by the chat launcher. */
    public chatStore: ChatStore
  ) {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.activeRoute = event.urlAfterRedirects;
      }
    });
  }

  ngOnInit() {
    this.getUser();
    this.resizeSubscription = this.dash.screenSize$.subscribe(size => {
      if (size.width < 768) {
        this.isMobileDevice = true;
      } else {
        this.isMobileDevice = false;
      }
    });
    this.getRoleByMenu();
  }

  ngOnDestroy() {
    if (this.resizeSubscription) {
      this.resizeSubscription.unsubscribe();
    }
  }

  user_permissions = [];
  getUser() {
    this.redux.getUser().subscribe(response => {
      if (response) {
        this.user_permissions = response.data.permissions?.data || [];
        // Overview (Login Activity) is master-admin / ops-manager only — those roles land on it,
        // everyone else keeps the old Reports/My-Sales entry (they'd only be bounced off Overview).
        const role = (Crypto.decrypt(localStorage.getItem('role')) || '').toString().toLowerCase();
        const isReportAdmin = role === 'master admin' || role === 'operations manager';
        const reportLink = isReportAdmin ? '/report/overview' : this.flagService.isFeatureEnabled('reports') ? '/report/reports' : '/report/my-sales';
        this.menu = [
          {
            title: 'Dashboard',
            slug: 'dashboard',
            icon: 'assets/img/dashboadIcon/home.svg',
            link: '/',
            flag: 'dashboard',
            operationalManager: 1,
            salesAgent: 1,
            administrator: 1,
            others: 1,
          },
          // {
          //   title: 'History',
          //   slug: 'history',
          //   icon: 'assets/img/dashboadIcon/history.svg',
          //   permissions: ['history.view'],
          //   flag: 'history',
          //   operationalManager: 2,
          //   salesAgent: 2,
          //   administrator: 12,
          //   others: 11,
          // },
          {
            title: 'Pin',
            slug: 'Pin',
            icon: 'assets/img/dashboadIcon/pin.svg',
            flag: 'pin',
            permissions: [],
            operationalManager: 3,
            salesAgent: 3,
            administrator: 13,
            others: 12,
          },
          {
            title: 'Email',
            slug: 'gmail',
            icon: 'assets/img/dashboadIcon/email.svg',
            link: '/my-inbox',
            permissions: ['leademail.manage'],
            flag: 'email',
            operationalManager: 4,
            salesAgent: 4,
            administrator: 2,
            others: 2,
          },
          {
            title: 'Calendar',
            slug: 'calender',
            icon: 'assets/img/dashboadIcon/calendar-v2.svg',
            link: '/calender',
            permissions: ['calendar.manage'],
            flag: 'calendar',
            operationalManager: 5,
            salesAgent: 5,
            administrator: 3,
            others: 3,
          },
          {
            title: 'My Calendar',
            slug: 'my-calender',
            icon: 'assets/img/dashboadIcon/calendar-v2.svg',
            link: '/my-calander/day',
            permissions: ['calendar.manage'],
            flag: 'new_calender',
            operationalManager: 5,
            salesAgent: 5,
            administrator: 3,
            others: 3,
          },
          {
            title: 'Reports',
            slug: 'reports',
            icon: 'assets/img/dashboadIcon/report.svg',
            link: reportLink,
            permissions: ['report.manage', 'report.view', 'report.create', 'report.edit', 'report.delete'],
            flag: 'basicReports',
            operationalManager: 6,
            salesAgent: 12,
            administrator: 10,
            others: 10,
          },
          {
            title: 'Contracts',
            icon: 'assets/img/dashboadIcon/contract.svg',
            link: '/contracts',
            permissions: ['contract.manage', 'contract.view'],
            flag: 'contracts',
            operationalManager: 7,
            salesAgent: 11,
            administrator: 8,
            others: 9,
          },
          {
            title: 'Projects',
            slug: 'my-listings',
            icon: 'assets/img/dashboadIcon/project1.svg',
            link: '/project/projects',
            permissions: ['allprojects.view', 'allprojects.manage'],
            flag: 'projects',
            operationalManager: 8,
            salesAgent: 10,
            administrator: 7,
            others: 8,
          },
          {
            title: 'Listings',
            slug: 'listings',
            icon: 'assets/img/dashboadIcon/listing.svg',
            link: '/listings/listing-properties',
            permissions: ['allproperties.manage', 'allproperties.view'],
            flag: 'listing',
            operationalManager: 9,
            salesAgent: 9,
            administrator: 6,
            others: 7,
          },
          {
            title: 'Lead',
            slug: 'lead-management',
            icon: 'assets/img/dashboadIcon/lead.svg',
            link: '/leads',
            permissions: ['lead.manage', 'lead.view'],
            flag: 'lead',
            operationalManager: 10,
            salesAgent: 6,
            administrator: 9,
            others: 5,
          },
          {
            title: 'Contacts',
            icon: 'assets/img/dashboadIcon/phoneBook.svg',
            link: '/setting/office-contact',
            permissions: ['contact.view', 'developer.view', 'user.view', 'staff.view', 'team.manage', 'offices.manage', 'groups.manage'],
            flag: 'contacts',
            operationalManager: 11,
            salesAgent: 7,
            administrator: 5,
            others: 6,
          },
          {
            title: 'Task',
            slug: 'task-manager',
            icon: 'assets/img/dashboadIcon/task.svg',
            link: '/tasks/tasks-overview',
            permissions: ['task.show'],
            flag: 'my_task',
            operationalManager: 12,
            salesAgent: 8,
            administrator: 4,
            others: 4,
          },
          {
            title: 'Library',
            slug: 'library',
            icon: 'assets/img/dashboadIcon/folder.svg',
            permissions: ['library.office.view', 'library.settings.manage', 'setting.library.setup'],
            link: '/library/private',
            flag: 'library',
            operationalManager: 13,
            salesAgent: 13,
            administrator: 11,
            others: 13,
          },
          {
            title: 'Market Place',
            slug: 'market-place',
            icon: 'assets/img/dashboadIcon/marketPlace.svg',
            link: '/listings/market-place',
            permissions: ['market.view'],
            flag: 'market_place',
            operationalManager: 14,
            salesAgent: 14,
            administrator: 14,
            others: 14,
          },
          {
            // Internal chat is open to everyone in the office — no permission gate, only the
            // `chat` feature flag. The row carries an unread badge (see the template).
            title: 'Chat',
            slug: 'chat',
            icon: 'assets/img/dashboadIcon/chat.svg',
            link: '/chat',
            permissions: [],
            flag: 'chat',
            operationalManager: 16,
            salesAgent: 16,
            administrator: 16,
            others: 16,
          },
          {
            title: 'Settings',
            slug: 'settings',
            icon: 'assets/img/dashboadIcon/setting.svg',
            link: '/setting/setup/visibility',
            permissions: [this.checkSettingPerm() ? 'setting.office.setup' : 'no_permission'],
            flag: 'setting',
            operationalManager: 15,
            salesAgent: 15,
            administrator: 15,
            others: 15,
          },

          // {
          //   title: 'Contact',
          //   slug: 'staff-contact',
          //   icon: 'assets/img/dashboadIcon/phoneBook.svg',
          //   link: '/staff-contact',
          //   flag: 'external_staff',
          //   permissions: ['externalestaff.view'],
          //   operationalManager: 16,
          //   salesAgent: 16,
          //   administrator: 16,
          //   others: 16,
          // },
        ];
        // Chat has no permission key, so permission stripping alone can't hide it from an acting assistant.
        this.actingAs.syncAllowedModules(response.data);
        const allowedModules = this.actingAs.getAllowedModules();
        if (allowedModules) {
          this.menu = this.menu.filter(item => isMenuLinkAllowed(item.link, allowedModules));
        }
      }
    });
  }

  checkSettingPerm() {
    let permissions = [
      'setting.visibility.setup',
      'setting.access.setup',
      'setting.library.setup',
      'setting.commission_setup.manage',
      'setting.tag_manager.setup',
      'setting.office_branding.setup',
      'setting.tag_approval.setup',
      'setting.kanban_order.setup',
      'setting.email_template.setup',
      'setting.email_account.setup',
      'setting.email_domain.setup',
      'setting.system_email.setup',
    ];

    let results = this.user_permissions.filter(ele => permissions.includes(ele));

    return results?.length > 0 ? true : false;
  }

  collapseClicked() {
    this.isCollapsed = !this.isCollapsed;
    this.toggleClick.emit(this.isCollapsed);
  }

  getRoleByMenu = () => {
    let role: string = Crypto.decrypt(localStorage.getItem('role')) || '';
    let roleType = role.toLowerCase();

    if (role === 'operations manager') {
      roleType = 'operationalManager';
    } else if (role === 'sales agent') {
      roleType = 'salesAgent';
    } else if (role === 'master admin') {
      roleType = 'administrator';
    } else {
      roleType = 'others';
    }
    this.menu.sort((a, b) => a[roleType] - b[roleType]);
  };

  movePage(page) {
    this.hidePrecient();
    if (page?.title?.toLowerCase() === 'settings') {
      this.router.navigate(['/setting/failed-dynamic-sync']);
    } else {
      this.router.navigate([page.link]);
    }
  }

  hidePrecient() {
    localStorage.removeItem('precinct');
  }
}
